import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { secureStorage } from '../utils/secureStorage'

interface MenuItemOrder {
  id: string
  order: number
}

interface MenuItemColors {
  textColor?: string
  backgroundColor?: string
}

interface UserGroup {
  id: string
  label: string
  icon: string
  items: string[] // Array of paths
}

interface MenuState {
  sidebarOrder: string[] // Array of menu item IDs or paths
  footerOrder: string[]
  pinnedItems: string[] // Array of paths for pinned items
  hiddenItems: string[] // Array of paths or labels for hidden items
  itemColors: Record<string, MenuItemColors> // ID -> colors
  itemIcons: Record<string, string> // ID -> icon name (FiHome, etc.)
  itemLabels: Record<string, string> // ID -> custom label
  recentItems: string[] // Array of paths
  userGroups: UserGroup[] // Custom groups created by user
  usageStats: Record<string, number> // path -> click count
  isCustomizing: boolean
  isFocusMode: boolean
  isCommandPaletteOpen: boolean
  
  setSidebarOrder: (order: string[]) => void
  setFooterOrder: (order: string[]) => void
  setPinnedItems: (pinned: string[]) => void
  togglePinnedItem: (path: string) => void
  toggleHiddenItem: (id: string) => void
  setItemColors: (id: string, colors: MenuItemColors) => void
  resetItemColors: (id: string) => void
  setItemIcon: (id: string, iconName: string) => void
  setItemLabel: (id: string, label: string) => void
  addUserGroup: (group: UserGroup) => void
  removeUserGroup: (id: string) => void
  updateUserGroup: (id: string, updates: Partial<UserGroup>) => void
  toggleItemInGroup: (groupId: string, itemPath: string) => void
  addRecentItem: (path: string) => void
  incrementUsage: (path: string) => void
  setIsCustomizing: (val: boolean) => void
  setIsFocusMode: (val: boolean) => void
  setIsCommandPaletteOpen: (val: boolean) => void
  toggleCommandPalette: () => void
  resetOrder: () => void
}

export const useMenuStore = create<MenuState>()(
  persist(
    (set) => ({
      sidebarOrder: [],
      footerOrder: [],
      pinnedItems: [],
      hiddenItems: [],
      itemColors: {},
      itemIcons: {},
      itemLabels: {},
      recentItems: [],
      userGroups: [],
      usageStats: {},
      isCustomizing: false,
      isFocusMode: false,
      isCommandPaletteOpen: false,

      setSidebarOrder: (sidebarOrder) => set({ sidebarOrder }),
      setFooterOrder: (footerOrder) => set({ footerOrder }),
      setPinnedItems: (pinnedItems) => set({ pinnedItems }),
      
      togglePinnedItem: (path) => set((state) => {
        const exists = state.pinnedItems.includes(path)
        if (exists) {
          return { pinnedItems: state.pinnedItems.filter(p => p !== path) }
        } else {
          // Increase limit to 20 pinned items for better user experience
          if (state.pinnedItems.length >= 20) return state
          return { pinnedItems: [...state.pinnedItems, path] }
        }
      }),

      toggleHiddenItem: (id) => set((state) => ({
        hiddenItems: state.hiddenItems.includes(id)
          ? state.hiddenItems.filter(i => i !== id)
          : [...state.hiddenItems, id]
      })),

      setItemColors: (id, colors) => set((state) => ({
        itemColors: {
          ...state.itemColors,
          [id]: { ...state.itemColors[id], ...colors }
        }
      })),

      resetItemColors: (id) => set((state) => {
        const newItemColors = { ...state.itemColors }
        delete newItemColors[id]
        return { itemColors: newItemColors }
      }),

      setItemIcon: (id, iconName) => set((state) => ({
        itemIcons: { ...state.itemIcons, [id]: iconName }
      })),

      setItemLabel: (id, label) => set((state) => ({
        itemLabels: { ...state.itemLabels, [id]: label }
      })),

      addUserGroup: (group) => set((state) => ({
        userGroups: [...state.userGroups, group]
      })),

      removeUserGroup: (id) => set((state) => ({
        userGroups: state.userGroups.filter(g => g.id !== id)
      })),

      updateUserGroup: (id, updates) => set((state) => ({
        userGroups: state.userGroups.map(g => g.id === id ? { ...g, ...updates } : g)
      })),

      toggleItemInGroup: (groupId, itemPath) => set((state) => ({
        userGroups: state.userGroups.map(g => {
          if (g.id !== groupId) return g
          const exists = g.items.includes(itemPath)
          return {
            ...g,
            items: exists ? g.items.filter(p => p !== itemPath) : [...g.items, itemPath]
          }
        })
      })),

      addRecentItem: (path) => set((state) => {
        // Only track real paths
        if (!path || path === '#' || path === '/') return state
        
        const filtered = state.recentItems.filter(p => p !== path)
        const next = [path, ...filtered].slice(0, 5)
        return { recentItems: next }
      }),
      
      incrementUsage: (path) => set((state) => ({
        usageStats: {
          ...state.usageStats,
          [path]: (state.usageStats[path] || 0) + 1
        }
      })),

      setIsCustomizing: (isCustomizing) => set({ isCustomizing }),
      setIsFocusMode: (isFocusMode) => set({ isFocusMode }),
      setIsCommandPaletteOpen: (isCommandPaletteOpen) => set({ isCommandPaletteOpen }),
      toggleCommandPalette: () => set((state) => ({ isCommandPaletteOpen: !state.isCommandPaletteOpen })),

      resetOrder: () => set({ 
        sidebarOrder: [], 
        footerOrder: [], 
        pinnedItems: [], 
        hiddenItems: [],
        itemColors: {},
        itemIcons: {},
        itemLabels: {},
        recentItems: [],
        userGroups: [],
        isFocusMode: false,
        isCommandPaletteOpen: false
      }),
    }),
    {
      name: 'menu-storage',
      storage: {
        getItem: (name) => {
          const str = secureStorage.getItem(name)
          return str ? JSON.parse(str) : null
        },
        setItem: (name, value) => secureStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => secureStorage.removeItem(name),
      },
    }
  )
)
